import React, { useMemo } from 'react';
import { OKRState, Task } from '../types';
import {
    computeObjectiveProgress, computeKPIHealth, computeSmartScore,
    statusColors, statusLabels, priorityColors, priorityLabels,
    taskStatusConfig, getTeamMemberInitials, getTeamMemberColor, healthColors,
} from '../helpers';
import { useOKRPermissions } from '../useOKRPermissions';
import { AlertTriangle, Zap, Calendar, CheckCircle2 } from 'lucide-react';
import { MdOutlineTrackChanges, MdBarChart, MdGroup, MdNotificationsNone } from 'react-icons/md';
import Card from 'components/card';

// ── Stat card (matches MiniStatistics style used across the app) ──────────────

const Stat: React.FC<{
    icon: React.ReactNode;
    label: string;
    value: string | number;
    sub?: string;
    onClick?: () => void;
}> = ({ icon, label, value, sub, onClick }) => (
    <div
        onClick={onClick}
        className={`flex w-full border items-center gap-[10px] rounded-[20px] bg-white bg-clip-border px-4 py-4 shadow-3xl shadow-shadow-500 ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
    >
        <div className="flex min-h-[50px] min-w-[50px] items-center justify-center rounded-full text-[26px] text-toola-500 bg-lightPrimary">
            {icon}
        </div>
        <div>
            <p className="mb-0.5 text-sm font-medium text-gray-600">{label}</p>
            <h3 className="text-xl font-bold leading-6 text-navy-700">{value}</h3>
            {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
        </div>
    </div>
);

// ── Dashboard ─────────────────────────────────────────────────────────────────

const Dashboard: React.FC<{
    state: OKRState;
    onStatusChange: (id: string, s: Task['status']) => void;
    onTabChange: (t: string) => void;
}> = ({ state, onStatusChange, onTabChange }) => {
    const { canSeeFinancials, canSeeTeamDetails, isOwner } = useOKRPermissions();
    const { objectives, kpis, tasks, team, alerts } = state;

    // Memoize all derived stats to avoid recomputing on every re-render
    const stats = useMemo(() => {
        const onTrack = objectives.filter((o) => o.status === 'on-track').length;
        const behind = objectives.filter((o) => o.status === 'behind').length;
        const atRisk = objectives.filter((o) => o.status === 'at-risk').length;
        const kpiGood = kpis.filter((k) => computeKPIHealth(k) === 'good').length;
        const kpiWarn = kpis.filter((k) => computeKPIHealth(k) === 'warning').length;
        const kpiCrit = kpis.filter((k) => computeKPIHealth(k) === 'critical').length;
        const activeTasks = tasks.filter((t) => t.status !== 'done');
        const unread = alerts.filter((a) => !a.acknowledged);
        const critAlerts = unread.filter((a) => a.severity === 'critical');
        const totalLoad = team.reduce((s, m) => s + m.currentActiveTasks, 0);
        const avgLoad = team.length ? Math.round((totalLoad / team.length) * 10) / 10 : 0;
        const criticalTasks = [...activeTasks]
            .sort((a, b) => computeSmartScore(b) - computeSmartScore(a))
            .slice(0, 6);
        const totalRevImpact = tasks
            .filter((t) => t.status === 'done' && t.businessImpact)
            .reduce((s, t) => s + (t.businessImpact?.revenueEur ?? 0), 0);
        return { onTrack, behind, atRisk, kpiGood, kpiWarn, kpiCrit, activeTasks, unread, critAlerts, avgLoad, criticalTasks, totalRevImpact };
    }, [objectives, kpis, tasks, team, alerts]);

    const { onTrack, behind, atRisk, kpiGood, kpiWarn, kpiCrit, activeTasks, unread, critAlerts, avgLoad, criticalTasks, totalRevImpact } = stats;

    return (
        <div className="space-y-6">
            {/* Stat row */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Stat
                    icon={<MdOutlineTrackChanges />}
                    label="Objectifs en avance"
                    value={`${onTrack} / ${objectives.length}`}
                    sub={`${behind} en retard · ${atRisk} à risque`}
                    onClick={() => onTabChange('objectives')}
                />
                <Stat
                    icon={<MdBarChart />}
                    label="KPIs en bonne santé"
                    value={`${kpiGood} / ${kpis.length}`}
                    sub={`${kpiWarn} warning · ${kpiCrit} critiques`}
                    onClick={() => onTabChange('objectives')}
                />
                <Stat
                    icon={<MdGroup />}
                    label="Charge équipe moy."
                    value={`${avgLoad} tâches`}
                    sub={`${activeTasks.length} tâches actives`}
                    onClick={() => onTabChange('team')}
                />
                <Stat
                    icon={<MdNotificationsNone />}
                    label="Alertes non lues"
                    value={unread.length}
                    sub={`${critAlerts.length} critique${critAlerts.length > 1 ? 's' : ''}`}
                    onClick={() => onTabChange('alerts')}
                />
            </div>

            {/* Business impact banner — owners only */}
            {canSeeFinancials && totalRevImpact > 0 && (
                <Card extra="p-5 border border-toola-100 bg-gradient-to-r from-toola-500 to-toola-600 text-white">
                    <div className="flex items-center justify-between gap-4">
                        <div>
                            <p className="text-sm font-medium text-blue-100 mb-1">Impact Business estimé — tâches terminées</p>
                            <p className="text-3xl font-bold">
                                +{new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(totalRevImpact)}
                            </p>
                            <p className="text-xs text-blue-200 mt-1">
                                {tasks.filter((t) => t.status === 'done' && t.businessImpact).length} tâche(s) à impact calculé
                            </p>
                        </div>
                        <CheckCircle2 size={52} className="text-white/20 flex-shrink-0" />
                    </div>
                </Card>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Objectives overview */}
                <Card extra="p-5 lg:col-span-2">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-base font-bold text-navy-700">Vue d'ensemble des objectifs</h3>
                        <button onClick={() => onTabChange('objectives')} className="text-sm text-toola-500 hover:text-toola-600 font-medium">
                            Voir tout →
                        </button>
                    </div>
                    <div className="space-y-4">
                        {objectives.map((obj) => {
                            const progress = computeObjectiveProgress(obj, kpis);
                            const pc = progress >= 70 ? '#17ad37' : progress >= 40 ? '#FFB547' : '#ea0606';
                            const objKpis = kpis.filter((k) => obj.kpiIds.includes(k.id) || obj.kpiIds.includes(k._id ?? ''));
                            return (
                                <div key={obj.id}>
                                    <div className="flex items-center justify-between mb-1.5">
                                        <div className="flex items-center gap-2 min-w-0 flex-1">
                                            <p className="text-sm font-semibold text-navy-700 truncate">{obj.name}</p>
                                            <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${statusColors[obj.status]}`}>
                                                {statusLabels[obj.status]}
                                            </span>
                                        </div>
                                        <span className="text-sm font-bold ml-3 flex-shrink-0" style={{ color: pc }}>{progress}%</span>
                                    </div>
                                    <div className="w-full h-1.5 rounded-full bg-gray-100 overflow-hidden">
                                        <div className="h-full rounded-full transition-all" style={{ width: `${progress}%`, backgroundColor: pc }} />
                                    </div>
                                    <div className="flex items-center gap-1.5 mt-1.5">
                                        {objKpis.map((k) => {
                                            const h = computeKPIHealth(k);
                                            return <span key={k.id} title={k.name} className="w-2 h-2 rounded-full" style={{ backgroundColor: (healthColors as any)[h] }} />;
                                        })}
                                        <span className="text-xs text-gray-400">{objKpis.length} KPI{objKpis.length > 1 ? 's' : ''}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </Card>

                {/* Critical alerts */}
                <Card extra="p-5">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-base font-bold text-navy-700">Alertes actives</h3>
                        <button onClick={() => onTabChange('alerts')} className="text-sm text-toola-500 hover:text-toola-600 font-medium">
                            Voir tout →
                        </button>
                    </div>
                    <div className="space-y-3">
                        {unread.slice(0, 4).map((a) => {
                            const cls =
                                a.severity === 'critical' ? 'bg-red-50 border-red-200 text-red-600' :
                                a.severity === 'warning'  ? 'bg-orange-50 border-orange-200 text-orange-500' :
                                'bg-blue-50 border-blue-200 text-blue-500';
                            return (
                                <div key={a.id} className={`rounded-xl border p-3 ${cls}`}>
                                    <div className="flex items-start gap-2">
                                        <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
                                        <div>
                                            <p className="text-xs font-bold">{a.title}</p>
                                            <p className="text-xs opacity-80 mt-0.5 leading-relaxed">{a.message}</p>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                        {unread.length === 0 && (
                            <div className="text-center py-6">
                                <CheckCircle2 size={32} className="mx-auto text-green-300 mb-2" />
                                <p className="text-sm text-gray-400">Aucune alerte active</p>
                            </div>
                        )}
                    </div>
                </Card>
            </div>

            {/* Critical tasks by smart score */}
            <Card extra="p-5">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h3 className="text-base font-bold text-navy-700 flex items-center gap-2">
                            <Zap size={16} className="text-orange-400" /> Tâches à plus fort impact
                        </h3>
                        <p className="text-xs text-gray-400 mt-0.5">Triées par smart score = (Impact × Urgence) / Effort</p>
                    </div>
                    <button onClick={() => onTabChange('tasks')} className="text-sm text-toola-500 hover:text-toola-600 font-medium">
                        Voir toutes →
                    </button>
                </div>
                <div className="space-y-2">
                    {criticalTasks.map((task) => {
                        const score = computeSmartScore(task);
                        const cfg = taskStatusConfig[task.status];
                        const initials = getTeamMemberInitials(task.assigneeId, state);
                        const avatarColor = getTeamMemberColor(task.assigneeId, state);
                        const isOverdue = new Date(task.dueDate) < new Date() && task.status !== 'done';
                        const scoreColor = score >= 15 ? '#ea0606' : score >= 8 ? '#FFB547' : '#A3AED0';
                        return (
                            <div key={task.id} className="flex items-center gap-3 rounded-xl border border-gray-100 p-3 hover:bg-gray-50 transition-colors">
                                <div
                                    className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                                    style={{ backgroundColor: avatarColor }}
                                >
                                    {initials}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-navy-700 truncate">{task.title}</p>
                                    <span className={`text-xs border rounded-full px-2 py-0.5 ${priorityColors[task.priority]}`}>
                                        {priorityLabels[task.priority]}
                                    </span>
                                </div>
                                <span className={`text-xs flex items-center gap-1 flex-shrink-0 ${isOverdue ? 'text-red-500 font-semibold' : 'text-gray-400'}`}>
                                    <Calendar size={11} />
                                    {new Date(task.dueDate).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                                </span>
                                <span className="text-sm font-bold flex-shrink-0 w-8 text-right" style={{ color: scoreColor }}>
                                    {score}
                                </span>
                                <select
                                    value={task.status}
                                    onChange={(e) => onStatusChange(task.id, e.target.value as Task['status'])}
                                    className="text-xs rounded-lg border-0 py-1 px-2 font-medium cursor-pointer flex-shrink-0"
                                    style={{ backgroundColor: cfg.bg, color: cfg.color }}
                                >
                                    {Object.entries(taskStatusConfig).map(([k, v]) => (
                                        <option key={k} value={k}>{v.label}</option>
                                    ))}
                                </select>
                            </div>
                        );
                    })}
                    {criticalTasks.length === 0 && (
                        <p className="text-sm text-gray-400 text-center py-4">Aucune tâche active</p>
                    )}
                </div>
            </Card>

            {/* Team load — owner only */}
            {canSeeTeamDetails && <Card extra="p-5">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-base font-bold text-navy-700">Charge de l'équipe</h3>
                    <button onClick={() => onTabChange('team')} className="text-sm text-toola-500 hover:text-toola-600 font-medium">
                        Détails →
                    </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {team.map((m) => {
                        const lp = Math.min(100, (m.currentActiveTasks / 8) * 100);
                        const lc = lp >= 80 ? '#ea0606' : lp >= 60 ? '#FFB547' : '#17ad37';
                        return (
                            <div key={m.id} className="rounded-xl border border-gray-100 p-4">
                                <div className="flex items-center gap-3 mb-3">
                                    <div
                                        className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                                        style={{ backgroundColor: m.avatarColor }}
                                    >
                                        {m.avatarInitials}
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-sm font-bold text-navy-700 truncate">{m.name}</p>
                                        <p className="text-xs text-gray-400 truncate">{m.role}</p>
                                    </div>
                                </div>
                                <div className="space-y-1.5">
                                    <div className="flex justify-between text-xs text-gray-500">
                                        <span>Charge</span>
                                        <span className="font-bold" style={{ color: lc }}>{m.currentActiveTasks} tâches</span>
                                    </div>
                                    <div className="w-full h-1.5 rounded-full bg-gray-100 overflow-hidden">
                                        <div className="h-full rounded-full" style={{ width: `${lp}%`, backgroundColor: lc }} />
                                    </div>
                                    <div className="flex justify-between text-xs text-gray-400">
                                        <span>Délais respectés</span>
                                        <span className="font-medium text-navy-700">{m.onTimeRate}%</span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </Card>}
        </div>
    );
};

export default Dashboard;
